<template>
    <div>
        <!-- Botão Flutuante -->
        <div class="fixed bottom-6 right-6 z-[90]">
            <button type="button"
                class="relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-white shadow-xl transition hover:scale-105 hover:bg-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-300"
                @click="toggleModal">
                <font-awesome-icon v-if="!isOpen" icon="fa-comment-dots" class="text-2xl text-black" />
                <font-awesome-icon v-else icon="fa-xmark" class="text-2xl text-black" />

                <span v-if="totalChatsWithUnread > 0 && !isOpen"
                    class="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold shadow-lg">
                    {{ totalChatsWithUnread > 99 ? '99+' : totalChatsWithUnread }}
                </span>
            </button>
        </div>

        <!-- Modal Principal -->
        <teleport to="body">
            <transition name="chat-fade">
                <div v-if="isOpen" class="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
                    <div class="absolute inset-0 bg-black/50" @click="closeModal" />
                    <div
                        class="relative z-[101] flex h-[100vh] w-full flex-col bg-white shadow-2xl sm:h-[85vh] sm:max-w-5xl sm:rounded-2xl overflow-hidden">

                        <!-- Header -->
                        <div
                            class="flex-shrink-0 p-4 flex items-center justify-between gap-4 border-b border-gray-200 sm:rounded-t-2xl bg-white">
                            <h2 class="text-lg font-semibold uppercase text-gray-800">{{ modalTitle }}</h2>
                            <button type="button" class="text-gray-500 hover:text-gray-700 p-2" @click="closeModal">
                                <font-awesome-icon icon="fa-xmark" class="text-xl" />
                            </button>
                        </div>

                        <!-- Main Content -->
                        <div class="flex-1 flex flex-col lg:flex-row overflow-hidden">
                            <!-- Lista de Conversas -->
                            <chat-conversation-list :conversations="conversations"
                                :selected-conversation="selectedConversation" @select="selectConversation"
                                :get-name="getConversationName" :get-preview="conversationPreview"
                                :is-online="isConversationOnline" :total-unread="totalChatsWithUnread"
                                @new-conversation="showNewConversationModal = true" />

                            <!-- Painel de Chat -->
                            <div v-if="selectedConversation"
                                class="relative bg-white flex-1 flex flex-col overflow-hidden"
                                :class="selectedConversation ? 'flex' : 'hidden lg:flex'">
                                <!-- Header do Chat -->
                                <header
                                    class="flex-shrink-0 flex items-center justify-between p-3 sm:p-4 shadow-sm border-b border-gray-200">
                                    <div class="flex items-center gap-3 min-w-0 flex-1">
                                        <button type="button"
                                            class="lg:hidden flex-shrink-0 text-gray-600 hover:text-gray-800 -ml-2 p-2"
                                            @click="backToConversations">
                                            <font-awesome-icon icon="fa-arrow-left" class="text-lg" />
                                        </button>
                                        <div class="relative flex-shrink-0">
                                            <img src="/images/no-photo.jpg" class="h-10 w-10 rounded-full" />
                                            <span
                                                class="absolute bottom-0 right-0 block w-3 h-3 rounded-full border-2 border-white"
                                                :class="isConversationOnline(selectedConversation) ? 'bg-green-500' : 'bg-gray-400'"></span>
                                        </div>
                                        <div class="min-w-0 flex-1 flex items-center gap-2">
                                            <p class="text-base font-bold leading-none truncate">
                                                {{ getConversationName(selectedConversation) }}
                                            </p>
                                            <span v-if="isPolling"
                                                class="ml-2 px-2 py-0.5 rounded bg-yellow-200 text-yellow-800 text-xs font-semibold">Modo
                                                Polling</span>
                                        </div>
                                    </div>
                                </header>
                                <!-- Lista de Mensagens -->
                                <chat-message-list ref="messageList" :messages="messages"
                                    :loading-messages="loadingMessages" :loading-older-messages="loadingOlderMessages"
                                    :has-more-messages="hasMoreMessages" @load-older="handleLoadOlder" />
                                <!-- Input de Mensagem -->
                                <chat-message-input :sending="sendingMessage" @send="handleSendMessage" />
                            </div>
                            <!-- Placeholder quando nenhuma conversa selecionada -->
                            <div v-else class="flex flex-1 items-center justify-center text-gray-500 p-4 text-center">
                                Selecione uma conversa para começar
                            </div>
                        </div>

                    </div>
                </div>
            </transition>
        </teleport>

        <!-- Modal de Nova Conversa -->
        <chat-new-conversation-modal :show="showNewConversationModal" :title="`Iniciar nova conversa`"
            :subtitle="newConversationSubtitle" :search-placeholder="newConversationSearchPlaceholder"
            :empty-message="`Nenhum item disponível para iniciar conversa.`" :entities="availableEntities"
            :loading="loadingEntities" :creating="creatingConversation" :get-entity-name="getEntityName"
            :get-entity-info="getEntityInfo" @close="closeNewConversationModal" @select="startConversation"
            @search="handleEntitySearch" />
    </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

import axios from 'axios'
import { useChat } from './composables/useChat'
import ChatConversationList from './components/ChatConversationList.vue'
import ChatMessageList from './components/ChatMessageList.vue'
import ChatMessageInput from './components/ChatMessageInput.vue'
import ChatNewConversationModal from './components/ChatNewConversationModal.vue'

export default {
    components: {
        ChatConversationList,
        ChatMessageList,
        ChatMessageInput,
        ChatNewConversationModal
    },

    props: {
        modalTitle: {
            type: String,
            default: 'Mensagens'
        },
        newConversationSubtitle: {
            type: String,
            default: 'Selecione um item para começar a conversar.'
        },
        newConversationSearchPlaceholder: {
            type: String,
            default: 'Buscar...'
        },
        apiBasePath: {
            type: String,
            required: true
        },
        entityKey: {
            type: String,
            default: 'agency'
        },
        entityIdKey: {
            type: String,
            default: 'agency_id'
        }
    },

    emits: ['update-unread-count'],

    setup(props, { emit }) {
        // Atualiza mensagens de forma incremental (sem piscar)
        function updateMessagesIncremental(newMessages) {
            if (!Array.isArray(newMessages)) return;
            // Adiciona apenas mensagens novas ao final
            const existingIds = new Set(messages.value.map(m => m.id));
            const toAdd = newMessages.filter(m => !existingIds.has(m.id));
            if (toAdd.length > 0) {
                messages.value.push(...toAdd);
            }
            // Opcional: atualizar mensagens existentes (ex: status de leitura)
            // for (const msg of newMessages) {
            //     const idx = messages.value.findIndex(m => m.id === msg.id);
            //     if (idx !== -1) messages.value[idx] = { ...messages.value[idx], ...msg };
            // }
        }
        // Bloqueia o body ao abrir o modal
        const handleKeydown = (e) => {
            if (e.key === 'Escape') closeModal()
        }
        const applyBodyLock = () => {
            if (typeof document === 'undefined') return
            if (!bodyClassApplied.value) {
                document.body.classList.add('chat-modal-open', 'overflow-hidden')
                bodyClassApplied.value = true
            }
            if (typeof window !== 'undefined') {
                window.addEventListener('keydown', handleKeydown)
            }
        }
        // Controle do modal
        const openModal = () => {
            isOpen.value = true
        }
        const closeModal = () => {
            isOpen.value = false
        }
        const toggleModal = () => {
            isOpen.value = !isOpen.value
        }
        const isOpen = ref(false)
        const bodyClassApplied = ref(false)
        const messageList = ref(null)
        const sendingMessage = ref(false)

        // Nova conversa
        const showNewConversationModal = ref(false)
        const availableEntities = ref([])
        const loadingEntities = ref(false)
        const creatingConversation = ref(false)

        // Usa o composable de chat
        const {
            conversations,
            selectedConversation,
            messages,
            loadingMessages,
            loadingOlderMessages,
            hasMoreMessages,
            totalUnreadCount,
            totalChatsWithUnread,
            loadConversations,
            selectConversation: selectConv,
            sendMessage,
            markMessageAsRead,
            loadOlderMessages,
            clearConversation,
            isConversationOnline,
            getConversationName,
            getConversationInfo,
            getMessagePreview,
            resetOnlineStatus
        } = useChat({
            apiBasePath: props.apiBasePath,
            entityKey: props.entityKey,
            entityIdKey: props.entityIdKey,
            onMessageReceived: (message) => {
                // Scroll automático quando recebe mensagem via websocket
                nextTick(() => {
                    scrollToBottom()
                })
            }
        })


        // Função para checar se Echo está realmente conectado
        function isEchoConnected() {
            if (!window.Echo || !window.Echo.connector || !window.Echo.connector.pusher) return false;
            const state = window.Echo.connector.pusher.connection?.state;
            return state === 'connected';
        }

        const isPolling = ref(false)
        let pollingInterval = null


        // Função para iniciar polling
        function startPolling(val) {
            resetOnlineStatus();
            if (pollingInterval) {
                clearInterval(pollingInterval)
                pollingInterval = null
            }
            if (val) {
                isPolling.value = true
                pollingInterval = setInterval(async () => {
                    if (val) {
                        try {
                            const response = await axios.get(`${props.apiBasePath}/conversations/${val.id}/messages`);
                            let newMessages = [];
                            if (Array.isArray(response.data)) {
                                newMessages = response.data;
                            } else if (response.data.messages) {
                                newMessages = response.data.messages;
                            }
                            updateMessagesIncremental(newMessages);
                            for (const msg of messages.value) {
                                if (!msg.is_mine && !msg.read_at) {
                                    await markMessageAsRead(msg.id);
                                    msg.read_at = new Date().toISOString();
                                }
                            }
                            await loadConversations();
                        } catch (e) { }
                    }
                }, 5000)
            }
        }

        // Função para parar polling
        function stopPolling() {
            if (pollingInterval) {
                clearInterval(pollingInterval)
                pollingInterval = null
            }
            isPolling.value = false
        }

        // Observa selectedConversation e estado do websocket
        watch([selectedConversation], ([val]) => {
            if (val && (!window.Echo || !isEchoConnected())) {
                startPolling(val)
            } else {
                stopPolling()
            }
        })

        // Observa mudanças no estado do websocket para alternar polling/websocket em tempo real
        let wsStateInterval = null
        onMounted(() => {
            wsStateInterval = setInterval(() => {
                if (selectedConversation.value) {
                    if (!window.Echo || !isEchoConnected()) {
                        if (!isPolling.value) startPolling(selectedConversation.value)
                    } else {
                        if (isPolling.value) stopPolling()
                    }
                }
            }, 2000)
        })
        onBeforeUnmount(() => {
            if (wsStateInterval) clearInterval(wsStateInterval)
            stopPolling()
        })

        onBeforeUnmount(() => {
            if (pollingInterval) clearInterval(pollingInterval)
        })

        // Computed
        const conversationPreview = computed(() => (conversation) => {
            if (!conversation) return 'Sem mensagens'

            const text = conversation.last_message && conversation.last_message.trim()
            if (!text) return 'Sem mensagens'

            return stripMarkdown(text)
        })

        const stripMarkdown = (text) => {
            if (!text) return ''
            // Remove sintaxe Markdown básica para exibir preview limpo
            return text
                .replace(/\*\*(.*?)\*\*/g, '$1')     // Remove **negrito**
                .replace(/__(.*?)__/g, '$1')          // Remove __negrito__
                .replace(/\*(.*?)\*/g, '$1')          // Remove *itálico*
                .replace(/_(.*?)_/g, '$1')            // Remove _itálico_
                .replace(/~~(.*?)~~/g, '$1')          // Remove ~~riscado~~
                .replace(/\[(.*?)\]\(.*?\)/g, '$1')   // Remove [links](url) mantendo texto
                .replace(/`(.*?)`/g, '$1')            // Remove `código`
                .replace(/^>\s?/gm, '')               // Remove > citações
                .replace(/^#+\s?/gm, '')              // Remove # títulos
                .replace(/\n/g, ' ')                  // Remove quebras de linha
                .trim()
        }

        // Watchers
        watch(isOpen, (value) => {
            if (value) {
                applyBodyLock()
                loadConversations()
            } else {
                removeBodyLock()
                clearConversation()
            }
        })

        watch(totalChatsWithUnread, (newValue) => {
            emit('update-unread-count', newValue)
        })

        watch(loadingMessages, (value) => {
            if (value === false && messages.value.length > 0) {
                nextTick(() => {
                    setTimeout(() => {
                        scrollToBottom()
                    }, 150)
                })
            }
        })

        watch(showNewConversationModal, (value) => {
            if (value) {
                loadAvailableEntities()
            } else {
                availableEntities.value = []
            }
        })

        const removeBodyLock = () => {
            if (typeof document === 'undefined') return

            if (bodyClassApplied.value) {
                document.body.classList.remove('chat-modal-open', 'overflow-hidden')
                bodyClassApplied.value = false
            }

            if (typeof window !== 'undefined') {
                window.removeEventListener('keydown', handleKeydown)
            }
        }

        const selectConversation = async (conversation) => {
            await selectConv(conversation)
            nextTick(() => {
                scrollToBottom()
            })
        }

        const backToConversations = () => {
            clearConversation()
        }

        const handleSendMessage = async ({ message, attachments }) => {
            if (sendingMessage.value) return

            sendingMessage.value = true

            try {
                const sentMessage = await sendMessage(message, attachments)
                // Se a mensagem não veio na resposta (por erro no backend/websocket), recarrega a lista
                if (!sentMessage || !sentMessage.id) {
                    if (selectedConversation.value) {
                        await selectConversation(selectedConversation.value)
                    }
                } else if (messages.value.every(m => m.id !== sentMessage.id)) {
                    messages.value.push({ ...sentMessage, is_mine: true })
                }
                nextTick(() => {
                    scrollToBottom()
                })
            } catch (error) {
                console.error('Erro ao enviar mensagem:', error)
                const errorMessage = error.response?.data?.errors?.message?.[0]
                    || error.response?.data?.error
                    || 'Erro ao enviar mensagem'
                if (window && window.$toast) {
                    window.$toast.error(errorMessage)
                } else {
                    console.warn('Mensagem não enviada:', errorMessage)
                }
            } finally {
                sendingMessage.value = false
            }
        }

        const handleLoadOlder = async () => {
            if (!messageList.value) return

            const container = messageList.value.messagesContainer
            if (!container) return

            const previousScrollHeight = container.scrollHeight
            const previousScrollTop = container.scrollTop

            try {
                await loadOlderMessages()

                nextTick(() => {
                    const newScrollHeight = container.scrollHeight
                    const scrollDifference = newScrollHeight - previousScrollHeight
                    container.scrollTop = previousScrollTop + scrollDifference
                })
            } catch (error) {
                console.error('Erro ao carregar mensagens antigas:', error)
            }
        }

        const scrollToBottom = () => {
            if (messageList.value && messageList.value.scrollToBottom) {
                messageList.value.scrollToBottom()
            }
        }

        const closeNewConversationModal = () => {
            showNewConversationModal.value = false
        }

        const loadAvailableEntities = async (search = '') => {
            if (!showNewConversationModal.value) return

            loadingEntities.value = true

            try {
                const response = await axios.get(`${props.apiBasePath}/entities`, {
                    params: { search: search || undefined }
                })

                availableEntities.value = response.data || []
            } catch (error) {
                console.error('Erro ao carregar entidades:', error)
                availableEntities.value = []
            } finally {
                loadingEntities.value = false
            }
        }

        const handleEntitySearch = (search) => {
            loadAvailableEntities(search)
        }

        const startConversation = async (entity) => {
            if (creatingConversation.value) return

            creatingConversation.value = true

            try {
                const payload = {}
                payload[props.entityIdKey] = entity.id

                const response = await axios.post(`${props.apiBasePath}/conversations`, payload)

                const newConversation = response.data.conversation
                newConversation.unread_count = newConversation.unread_count ?? 0

                const existingIndex = conversations.value.findIndex(
                    conversation => conversation.id === newConversation.id
                )

                if (existingIndex !== -1) {
                    conversations.value.splice(existingIndex, 1)
                }

                conversations.value.unshift(newConversation)

                closeNewConversationModal()
                selectConversation(newConversation)
            } catch (error) {
                console.error('Erro ao iniciar conversa:', error)
                alert('Erro ao iniciar conversa. Tente novamente.')
            } finally {
                creatingConversation.value = false
            }
        }

        const getEntityName = (entity) => entity.name || 'Desconhecido'

        const getEntityInfo = (entity) => {
            if (entity.cnpj) return `CNPJ: ${entity.cnpj}`
            return 'Não informado'
        }

        // Lifecycle
        onMounted(() => {
            loadConversations()
        })

        onBeforeUnmount(() => {
            removeBodyLock()
        })

        return {
            isOpen,
            messageList,
            sendingMessage,
            showNewConversationModal,
            availableEntities,
            loadingEntities,
            creatingConversation,
            conversations,
            selectedConversation,
            messages,
            loadingMessages,
            loadingOlderMessages,
            hasMoreMessages,
            totalChatsWithUnread,
            conversationPreview,
            openModal,
            closeModal,
            toggleModal,
            selectConversation,
            backToConversations,
            handleSendMessage,
            handleLoadOlder,
            closeNewConversationModal,
            startConversation,
            handleEntitySearch,
            isConversationOnline,
            getConversationName,
            getConversationInfo,
            getEntityName,
            getEntityInfo,
            isPolling
            // Se precisar usar resetOnlineStatus fora do setup, adicione aqui:
            // resetOnlineStatus
        }
    }
}
</script>

<style>
.chat-fade-enter-active,
.chat-fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}

.chat-fade-enter-from,
.chat-fade-leave-to {
    opacity: 0;
}

@media (max-width: 1024px) {
    .chat-modal-open {
        overflow: hidden !important;
        position: fixed;
        width: 100%;
        height: 100%;
    }
}

@supports (-webkit-touch-callout: none) {
    .chat-modal-open {
        position: fixed;
        overflow: hidden;
        width: 100%;
        height: 100vh;
    }
}
</style>
